import { ActivityPreview } from "@/components/ActivityFeed";
import { DetailLayout } from "@/components/DetailLayout";
import { EntityFiles } from "@/components/EntityFiles";
import { PartyLink } from "@/components/PartyLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { WorkItemService } from "@/gen/omnios/v1/work_item_connect";
import { WorkItemKind } from "@/gen/omnios/v1/work_item_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
	Cloud,
	Download,
	FileText,
	Loader2,
	Users,
	X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkItemsDetailShortcuts } from "@/hooks/use-work-items-shortcuts";

export const Route = createFileRoute("/work-items/$id")({
	component: WorkItemDetailPage,
});

const client = createClient(WorkItemService, transport);
// ── Helpers ──

function useKindLabel() {
	const { t } = useTranslation("work-items");
	return (kind: WorkItemKind) => {
		switch (kind) {
			case WorkItemKind.CASE:
				return t("kind_case");
			case WorkItemKind.TASK:
				return t("kind_task");
			case WorkItemKind.SUB_TASK:
				return t("kind_sub_task_detail");
			default:
				return t("kind_default");
		}
	};
}

// ── Autosave (same pattern as product page) ──
// Skips saves until user has explicitly edited.
// Uses a ref for saveFn to avoid resetting the timer
// on every keystroke while still calling the latest fn.

function useAutosave(saveFn: () => void, dirty: boolean, delay = 1200) {
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const saveFnRef = useRef(saveFn);

	// Keep ref updated with latest saveFn
	useEffect(() => {
		saveFnRef.current = saveFn;
	});

	useEffect(() => {
		if (!dirty) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			saveFnRef.current();
		}, delay);
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [dirty, delay]);
}

function SaveIndicator({
	isPending,
	isSuccess,
	isError,
}: {
	isPending: boolean;
	isSuccess: boolean;
	isError: boolean;
}) {
	const { t } = useTranslation("work-items");
	if (isPending) {
		return (
			<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
				<Loader2 className="h-3.5 w-3.5 animate-spin" />
				{t("saving")}
			</span>
		);
	}
	if (isError) {
		return (
			<span className="flex items-center gap-1.5 text-xs text-destructive">
				<X className="h-3.5 w-3.5" />
				{t("save_error")}
			</span>
		);
	}
	if (isSuccess) {
		return (
			<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
				<Cloud className="h-3.5 w-3.5" />
				{t("saved")}
			</span>
		);
	}
	return null;
}

// ── File type icon helper ──

const FILE_TYPE_ICONS: Record<string, { icon: string; label: string }> = {
	"application/pdf": {
		icon: "/file-icons/pdf.png",
		label: "PDF",
	},
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
		icon: "/file-icons/excel.jpg",
		label: "Excel",
	},
	"application/vnd.ms-excel": {
		icon: "/file-icons/excel.jpg",
		label: "Excel",
	},
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
		icon: "/file-icons/word.jpg",
		label: "Word",
	},
	"application/msword": {
		icon: "/file-icons/word.jpg",
		label: "Word",
	},
};

function getFileTypeInfo(
	filename?: string,
	mimeType?: string,
): { icon: string; label: string } | null {
	if (mimeType && FILE_TYPE_ICONS[mimeType]) {
		return FILE_TYPE_ICONS[mimeType];
	}
	if (filename) {
		const ext = filename.split(".").pop()?.toLowerCase();
		if (ext === "pdf") return FILE_TYPE_ICONS["application/pdf"];
		if (ext === "xlsx" || ext === "xls")
			return FILE_TYPE_ICONS["application/vnd.ms-excel"];
		if (ext === "docx" || ext === "doc")
			return FILE_TYPE_ICONS["application/msword"];
	}
	return null;
}

// ── Source files card ──

const mediaClient = createClient(MediaService, transport);

function SourceFilesCard({
	attrs,
	title,
}: {
	attrs: Record<string, unknown> | undefined;
	title: string;
}) {
	const sourceText = (attrs?.source_text as string) ?? "";
	const sourceMediaId = (attrs?.source_media_id as string) ?? "";
	const sourceFilename = (attrs?.source_filename as string) ?? "";
	const sourceMimeType = (attrs?.source_mime_type as string) ?? "";

	const { t } = useTranslation("work-items");
	const [textExpanded, setTextExpanded] = useState(false);

	const { data: mediaUrl } = useQuery({
		queryKey: ["media-url", sourceMediaId],
		queryFn: async () => {
			const resp = await mediaClient.getMediaUrl({
				mediaId: sourceMediaId,
				ttlSeconds: 3600,
			});
			return resp.url;
		},
		enabled: !!sourceMediaId,
		staleTime: 30 * 60 * 1000,
	});

	if (!sourceText && !sourceMediaId) return null;

	const fileInfo = getFileTypeInfo(sourceFilename, sourceMimeType);

	const handleDownloadText = () => {
		const blob = new Blob([sourceText], {
			type: "text/plain;charset=utf-8",
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${title || "source"}.txt`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const handleDownloadFile = () => {
		if (!mediaUrl) return;
		const a = document.createElement("a");
		a.href = mediaUrl;
		a.download = sourceFilename || "file";
		a.target = "_blank";
		a.rel = "noopener noreferrer";
		a.click();
	};

	const truncatedText =
		sourceText.length > 200 ? sourceText.slice(0, 200) + "…" : sourceText;
	const textLines = sourceText.split("\n").length;

	return (
		<Card className="px-4 py-3 gap-3">
			<h3 className="text-sm font-medium">{t("source_data")}</h3>

			{/* Source file */}
			{sourceMediaId && (
				<Button
					variant="outline"
					onClick={handleDownloadFile}
					disabled={!mediaUrl}
					className="flex items-center gap-3 w-full h-auto p-3 border-border/60 hover:bg-muted/50 hover:border-border group text-left justify-start"
				>
					{fileInfo ? (
						<img
							src={fileInfo.icon}
							alt={fileInfo.label}
							className="h-10 w-10 rounded-lg object-cover shrink-0"
						/>
					) : (
						<div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
							<FileText className="h-5 w-5 text-muted-foreground" />
						</div>
					)}
					<div className="flex-1 min-w-0">
						<p className="text-sm font-medium truncate">
							{sourceFilename || t("file_label")}
						</p>
						{fileInfo && (
							<p className="text-xs text-muted-foreground">{fileInfo.label}</p>
						)}
					</div>
					<Download className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
				</Button>
			)}

			{/* Source text */}
			{sourceText && (
				<div className="space-y-1.5">
					{!sourceMediaId && (
						<div className="flex items-center justify-between">
							<span className="text-xs text-muted-foreground">
								{t("lines_count_label", { count: textLines })}
							</span>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleDownloadText}
								className="gap-1 text-xs text-muted-foreground hover:text-foreground h-auto p-0"
							>
								<Download className="h-3 w-3" />
								{t("download")}
							</Button>
						</div>
					)}
					<div
						className="relative cursor-pointer"
						onClick={() => setTextExpanded((v) => !v)}
					>
						<pre
							className={`text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2 font-mono whitespace-pre-wrap break-words overflow-hidden transition-all ${
								textExpanded ? "max-h-[400px] overflow-y-auto" : "max-h-[80px]"
							}`}
						>
							{textExpanded ? sourceText : truncatedText}
						</pre>
						{!textExpanded && sourceText.length > 200 && (
							<div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background/80 to-transparent rounded-b-md" />
						)}
					</div>
					{sourceText.length > 200 && (
						<Button
							variant="link"
							size="sm"
							onClick={() => setTextExpanded((v) => !v)}
							className="text-xs text-primary h-auto p-0"
						>
							{textExpanded ? t("collapse_text") : t("expand_text")}
						</Button>
					)}
				</div>
			)}
		</Card>
	);
}

// ── Main page ──

function WorkItemDetailPage() {
	const { t } = useTranslation("work-items");
	const kindLabel = useKindLabel();
	const { id } = Route.useParams();
	const qc = useQueryClient();

	const {
		data: wi,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["work-item", id],
		queryFn: () => client.getWorkItem({ id }),
	});

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [statusId, setStatusId] = useState("");
	const [typeId, setTypeId] = useState("");
	const [priorityId, setPriorityId] = useState("");
	const [dirty, setDirty] = useState(false);

	const { data: typeOpts } = useQuery({
		queryKey: ["config-options", "work_item_type"],
		queryFn: () =>
			client.listConfigOptions({
				domain: "work_item_type",
			}),
	});

	// Derive status domain from the type's attrs
	const statusDomain = (() => {
		const selectedType = typeOpts?.options?.find((o) => o.id === typeId);
		const attrs = (selectedType?.attrs ?? wi?.typeConfig?.attrs)?.toJson() as
			| Record<string, unknown>
			| undefined;
		return (attrs?.status_domain as string) || "work_item_status";
	})();

	const { data: statusOpts } = useQuery({
		queryKey: ["config-options", statusDomain],
		queryFn: () =>
			client.listConfigOptions({
				domain: statusDomain,
			}),
		enabled: !!statusDomain,
	});

	const { data: priorityOpts } = useQuery({
		queryKey: ["config-options", "work_item_priority"],
		queryFn: () =>
			client.listConfigOptions({
				domain: "work_item_priority",
			}),
	});

	useEffect(() => {
		if (wi) {
			setTitle(wi.title);
			setDescription(wi.description);
			setStatusId(wi.statusConfigId);
			setTypeId(wi.typeConfigId);
			setPriorityId(wi.priorityConfigId);
			setDirty(false);
		}
	}, [wi]);

	useBreadcrumbs([
		{ label: t("breadcrumb"), href: "/work-items" },
		{
			label: wi ? `#${wi.number} ${wi.title}` : "…",
		},
	]);

	const updateMut = useMutation({
		mutationFn: (patch: Record<string, unknown>) =>
			client.updateWorkItem({ id, ...patch }),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["work-item", id],
			});
			setDirty(false);
		},
	});

	const save = useCallback(
		() =>
			updateMut.mutate({
				title,
				description,
				statusConfigId: statusId || undefined,
				typeConfigId: typeId || undefined,
				priorityConfigId: priorityId || undefined,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[title, description, statusId, typeId, priorityId],
	);

	useAutosave(save, dirty);

	useWorkItemsDetailShortcuts({
		onSave: save,
		onBack: useCallback(() => window.history.back(), []),
	});

	if (isLoading) {
		return (
			<DetailLayout
				sidebar={
					<Card className="py-3 px-4">
						<Skeleton className="h-20 w-full rounded" />
					</Card>
				}
			>
				<Card className="space-y-4 px-4 py-3">
					<Skeleton className="h-10 w-2/3" />
					<Skeleton className="h-24 w-full rounded" />
				</Card>
			</DetailLayout>
		);
	}

	if (error || !wi) {
		return (
			<div className="flex items-center justify-center h-[60vh]">
				<div className="text-center">
					<h3 className="text-lg font-semibold mb-1">
						{t("error_loading_title")}
					</h3>
					<p className="text-sm text-muted-foreground">
						{error?.message ?? t("error_not_found")}
					</p>
					<Link
						to="/work-items"
						className="mt-4 inline-block text-sm text-primary underline"
					>
						{t("back_to_tracker")}
					</Link>
				</div>
			</div>
		);
	}

	const statuses = statusOpts?.options ?? [];
	const types = typeOpts?.options ?? [];
	const priorities = priorityOpts?.options ?? [];

	const sidebarContent = (
		<>
			{/* Properties */}
			{wi.kind !== WorkItemKind.SUB_TASK && (
				<Card className="py-3 gap-2">
					<div className="px-4 space-y-3">
						<Label>{t("properties_label")}</Label>

						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">{t("label_type")}</Label>
							<Select
								value={typeId}
								onValueChange={(v) => {
									setTypeId(v);
									updateMut.mutate({
										typeConfigId: v,
									});
								}}
							>
								<SelectTrigger className="h-9 text-sm">
									<SelectValue placeholder="—" />
								</SelectTrigger>
								<SelectContent>
									{types.map((opt) => (
										<SelectItem key={opt.id} value={opt.id}>
											{opt.displayName || opt.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">{t("deadline_label")}</Label>
							<Input
								type="date"
								className="h-9 text-sm"
								value={
									wi.deadline
										? new Date(Number(wi.deadline.seconds) * 1000)
												.toISOString()
												.slice(0, 10)
										: ""
								}
								readOnly
							/>
						</div>

						{wi.source && (
							<div className="space-y-1.5 text-sm">
								<Label className="text-xs text-muted-foreground">{t("source_label")}</Label>
								<p className="text-sm">{wi.source}</p>
							</div>
						)}

						{wi.createdAt && (
							<div className="space-y-1.5 text-sm">
								<Label className="text-xs text-muted-foreground">{t("created_label")}</Label>
								<p className="text-sm">
									{new Date(
										Number(wi.createdAt.seconds) * 1000,
									).toLocaleDateString("ru-RU")}
								</p>
							</div>
						)}
					</div>
				</Card>
			)}

			{/* Assignees */}
			<Card className="py-3 gap-2">
				<div className="px-4 space-y-2">
					<Label>{t("assigned_label")}</Label>
					{wi.assignees.length > 0 ? (
						<div className="space-y-1">
							{wi.assignees.map((a) => (
								<div
									key={a.partyId}
									className="flex items-center gap-2 text-sm"
								>
									<div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
										{a.role.charAt(0).toUpperCase()}
									</div>
									<span className="truncate">
										<PartyLink partyId={a.partyId} />
									</span>
									<Badge
										variant="outline"
										className="ml-auto text-xs shrink-0"
									>
										{a.role}
									</Badge>
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-muted-foreground">{t("assignees_add")}</p>
					)}
				</div>
			</Card>

			<EntityFiles entityType="work_item" entityId={id} compact />
		</>
	);

	return (
		<DetailLayout sidebar={sidebarContent}>

				{/* Header bar */}
				<Card className="px-4 py-3 gap-4">
					<div className="flex items-center gap-2 flex-wrap">
						<Badge variant="outline" className="text-[10px]">
							{kindLabel(wi.kind)}
						</Badge>
						<span className="font-mono text-xs text-muted-foreground">
							#{String(wi.number)}
						</span>
						{wi.parentId && (
							<>
								<span className="text-xs text-muted-foreground">·</span>
								<Link
									to="/work-items/$id"
									params={{
										id: wi.parentId,
									}}
									search={{}}
									className="text-xs text-primary hover:underline"
								>
									{t("parent_link")}
								</Link>
							</>
						)}
						{wi.kind !== WorkItemKind.SUB_TASK && (
							<>
								<Select
									value={statusId}
									onValueChange={(v) => {
										setStatusId(v);
										updateMut.mutate({
											statusConfigId: v,
										});
									}}
								>
									<SelectTrigger className="h-7 text-xs w-auto gap-1.5 px-2">
										<SelectValue placeholder={t("label_status")} />
									</SelectTrigger>
									<SelectContent>
										{statuses.map((opt) => (
											<SelectItem key={opt.id} value={opt.id}>
												<span className="flex items-center gap-2">
													{opt.color && (
														<span
															className="h-2 w-2 rounded-full"
															style={{
																backgroundColor: opt.color,
															}}
														/>
													)}
													{opt.displayName || opt.name}
												</span>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Select
									value={priorityId}
									onValueChange={(v) => {
										setPriorityId(v);
										updateMut.mutate({
											priorityConfigId: v,
										});
									}}
								>
									<SelectTrigger className="h-7 text-xs w-auto gap-1.5 px-2">
										<SelectValue placeholder={t("label_priority")} />
									</SelectTrigger>
									<SelectContent>
										{priorities.map((opt) => (
											<SelectItem key={opt.id} value={opt.id}>
												{opt.displayName || opt.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</>
						)}
						<div className="ml-auto">
							<SaveIndicator
								isPending={updateMut.isPending}
								isSuccess={updateMut.isSuccess}
								isError={updateMut.isError}
							/>
						</div>
					</div>

					<Input
						value={title}
						onChange={(e) => {
							setTitle(e.target.value);
							setDirty(true);
						}}
						className="text-xl font-semibold border-none shadow-none px-1 -mx-1 h-auto focus-visible:ring-0 rounded hover:bg-muted/50 focus:bg-muted/50 transition-colors"
						placeholder={t("title_placeholder")}
					/>

					<Textarea
						value={description}
						onChange={(e) => {
							setDescription(e.target.value);
							setDirty(true);
						}}
						rows={3}
						placeholder={t("description_placeholder")}
						className="resize-none"
					/>
				</Card>

				{/* Customer context card */}
				<Card className="px-4 py-4 gap-2">
					<div className="flex items-center gap-3">
						<div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
							<Users className="h-4.5 w-4.5 text-muted-foreground" />
						</div>
						{wi.customer ? (
							<div className="flex items-center justify-between flex-1 min-w-0">
								<div className="min-w-0">
									<p className="text-xs text-muted-foreground mb-0.5">
										{t("customer_label")}
									</p>
									<div className="text-sm font-medium truncate">
										<PartyLink partyId={wi.customer.partyId} />
									</div>
								</div>
								<Button
									variant="ghost"
									size="sm"
									className="text-xs text-muted-foreground shrink-0"
								>
									{t("customer_change")}
								</Button>
							</div>
						) : (
							<div className="flex items-center justify-between flex-1">
								<p className="text-sm text-muted-foreground">
									{t("customer_attach")}
								</p>
								<Button variant="outline" size="sm" className="text-xs">
									{t("customer_attach_btn")}
								</Button>
							</div>
						)}
					</div>
				</Card>

				{/* Source files */}
				{wi.kind === WorkItemKind.CASE && (
					<SourceFilesCard
						attrs={wi.attrs?.toJson() as Record<string, unknown> | undefined}
						title={wi.title}
					/>
				)}

				{/* Activity — full-width in main column */}
				<Card className="py-3 gap-2">
					<div className="px-4">
						<ActivityPreview entityType="work_item" entityId={id} />
					</div>
				</Card>
		</DetailLayout>
	);
}
